/**
 * Hook para gerenciar configurações de providers de assinatura digital
 * 
 * Busca providers disponíveis, configurações do tenant e oferece
 * mutations para criar/atualizar/deletar configurações.
 * 
 * RELEASE: v1.0.0 - 2026-02-16
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger com namespace para rastreamento
const logger = createLogger('SignatureConfigs');

// =============================================================================
// TIPOS
// =============================================================================

/** Provider de assinatura digital disponível no sistema */
export interface SignatureProvider {
  id: string;
  name: string;
  display_name: string;
  slug: string;
  is_active: boolean;
  settings_schema: Record<string, unknown> | null;
  created_at: string;
}

/** Configuração de um provider para o tenant */
export interface TenantSignatureConfig {
  id: string;
  tenant_id: string;
  provider_id: string;
  credentials: Record<string, string>;
  is_active: boolean;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /** Join com signature_providers */
  signature_providers?: SignatureProvider;
}

/** Dados para criar/atualizar configuração */
export interface UpsertSignatureConfigData {
  provider_id: string;
  credentials: Record<string, string>;
  is_active?: boolean;
  is_default?: boolean;
}

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

export function useTenantSignatureConfigs() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  // =========================================================================
  // Query: Busca providers disponíveis
  // =========================================================================
  const providersQuery = useQuery({
    queryKey: ['signature-providers'],
    queryFn: async () => {
      logger.debug('Buscando providers de assinatura disponíveis');

      const { data, error } = await supabase
        .from('signature_providers')
        .select('*')
        .eq('is_active', true)
        .order('display_name');

      if (error) {
        logger.error('Erro ao buscar providers', error);
        throw error;
      }

      return data as SignatureProvider[];
    },
  });

  // =========================================================================
  // Query: Busca configurações do tenant
  // =========================================================================
  const configsQuery = useQuery({
    queryKey: ['tenant-signature-configs', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      logger.debug('Buscando configurações de assinatura do tenant');

      const { data, error } = await supabase
        .from('tenant_signature_configs')
        .select(`
          *,
          signature_providers (id, name, display_name, slug, is_active, settings_schema)
        `)
        .eq('tenant_id', tenantId);

      if (error) {
        logger.error('Erro ao buscar configurações', error);
        throw error;
      }

      return data as TenantSignatureConfig[];
    },
    enabled: !!tenantId,
  });

  // =========================================================================
  // Mutation: Criar/Atualizar configuração
  // =========================================================================
  const upsertConfig = useMutation({
    mutationFn: async (payload: UpsertSignatureConfigData) => {
      if (!tenantId) throw new Error('Tenant não encontrado');
      logger.info('Salvando configuração de assinatura', { provider_id: payload.provider_id });

      // Verifica se já existe configuração para este provider
      const existing = configsQuery.data?.find(c => c.provider_id === payload.provider_id);

      if (existing) {
        // Atualiza configuração existente
        const { data, error } = await supabase
          .from('tenant_signature_configs')
          .update({
            credentials: payload.credentials as unknown as Record<string, never>,
            is_active: payload.is_active ?? existing.is_active,
            is_default: payload.is_default ?? existing.is_default,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .eq('tenant_id', tenantId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Cria nova configuração
        const { data, error } = await supabase
          .from('tenant_signature_configs')
          .insert({
            tenant_id: tenantId,
            provider_id: payload.provider_id,
            credentials: payload.credentials as unknown as Record<string, never>,
            is_active: payload.is_active ?? true,
            is_default: payload.is_default ?? false,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-signature-configs', tenantId] });
      toast.success('Configuração salva com sucesso');
    },
    onError: (error) => {
      logger.error('Erro ao salvar configuração', error);
      toast.error('Erro ao salvar configuração');
    },
  });

  // =========================================================================
  // Mutation: Alternar ativo/inativo
  // =========================================================================
  const toggleActive = useMutation({
    mutationFn: async ({ configId, isActive }: { configId: string; isActive: boolean }) => {
      if (!tenantId) throw new Error('Tenant não encontrado');
      logger.info('Alterando status do provider', { configId, isActive });

      const { error } = await supabase
        .from('tenant_signature_configs')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('id', configId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-signature-configs', tenantId] });
    },
    onError: (error) => {
      logger.error('Erro ao alterar status', error);
      toast.error('Erro ao alterar status do provider');
    },
  });

  // =========================================================================
  // Mutation: Definir como padrão
  // =========================================================================
  const setDefault = useMutation({
    mutationFn: async (configId: string) => {
      if (!tenantId) throw new Error('Tenant não encontrado');
      logger.info('Definindo provider padrão', { configId });

      // Remove padrão de todos
      await supabase
        .from('tenant_signature_configs')
        .update({ is_default: false, updated_at: new Date().toISOString() })
        .eq('tenant_id', tenantId);

      // Define o selecionado como padrão
      const { error } = await supabase
        .from('tenant_signature_configs')
        .update({ is_default: true, is_active: true, updated_at: new Date().toISOString() })
        .eq('id', configId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-signature-configs', tenantId] });
      toast.success('Provider padrão atualizado');
    },
    onError: (error) => {
      logger.error('Erro ao definir padrão', error);
      toast.error('Erro ao definir provider padrão');
    },
  });

  // =========================================================================
  // Mutation: Deletar configuração
  // =========================================================================
  const deleteConfig = useMutation({
    mutationFn: async (configId: string) => {
      if (!tenantId) throw new Error('Tenant não encontrado');
      logger.info('Removendo configuração', { configId });

      const { error } = await supabase
        .from('tenant_signature_configs')
        .delete()
        .eq('id', configId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-signature-configs', tenantId] });
      toast.success('Configuração removida');
    },
    onError: (error) => {
      logger.error('Erro ao remover configuração', error);
      toast.error('Erro ao remover configuração');
    },
  });

  // =========================================================================
  // Função: Testar conexão com provider via Edge Function
  // =========================================================================
  const testConnection = async (providerId: string, credentials: Record<string, string>) => {
    if (!tenantId) throw new Error('Tenant não encontrado');
    logger.info('Testando conexão com provider', { providerId });

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) throw new Error('Sessão não encontrada');

    const response = await supabase.functions.invoke('signature-proxy', {
      body: {
        operation: 'test_connection',
        tenant_id: tenantId,
        provider_id: providerId,
        credentials,
      },
    });

    if (response.error) throw response.error;
    return response.data;
  };

  // =========================================================================
  // Helpers
  // =========================================================================

  /** Retorna a configuração de um provider específico pelo slug */
  const getConfigBySlug = (slug: string) => {
    return configsQuery.data?.find(c => c.signature_providers?.slug === slug);
  };

  /** Máscara de credenciais para exibição (****...últimos4) */
  const maskCredential = (value: string): string => {
    if (!value || value.length < 8) return '••••••••';
    return `••••••••${value.slice(-4)}`;
  };

  return {
    // Dados
    providers: providersQuery.data ?? [],
    configs: configsQuery.data ?? [],
    isLoading: providersQuery.isLoading || configsQuery.isLoading,

    // Mutations
    upsertConfig,
    toggleActive,
    setDefault,
    deleteConfig,

    // Funções
    testConnection,
    getConfigBySlug,
    maskCredential,
  };
}
